import React, {useMemo, useState} from "react";
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import {enqueueSnackbar} from "notistack";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import dayjs, {Dayjs} from "dayjs";
import {Table} from "../components/table/Table";
import {TableHeaderProps} from "../components/table/TableTypes";
import {ScheduledLog} from "../types/ScheduledLog";
import {loadScheduledLogs, ScheduledLogFilter} from "../service/scheduler.service";
import {sanitizeFilterObject} from "../service/http.service";
import {DateTime} from "../components/form/DateTime";
import {FiltersCluster} from "../components/form/FilterCluster";

function formatDate(value?: string | null): string {
    if (!value) return "";
    const d = dayjs(value);
    return d.isValid() ? d.format("YYYY-MM-DD HH:mm:ss") : "";
}

function firstLine(logs?: string): string {
    if (!logs) return "";
    return logs.split("\n")[0];
}

export const SchedulerPage: React.FC = () => {
    const [filter, setFilter] = useState<ScheduledLogFilter>({
        page: 0,
        size: 25,
        sortKey: "createdAt",
        sortOrder: "desc",
    });

    const [viewLog, setViewLog] = useState<ScheduledLog | null>(null);

    const {data, isFetching} = useQuery({
        queryKey: ["scheduled-logs", filter],
        refetchOnWindowFocus: false,
        queryFn: () =>
            loadScheduledLogs(sanitizeFilterObject({...filter})).then(r => r.data).catch(e => {
                enqueueSnackbar({message: "Failed to load scheduler logs: " + e.message, variant: "error"});
                return undefined;
            }),
        placeholderData: keepPreviousData,
    });

    const headers: TableHeaderProps<ScheduledLog>[] = useMemo(() => [
        {
            key: "createdAt",
            title: "Start At",
            render: (row) => formatDate(row.createdAt),
        },
        {
            key: "endAt",
            title: "End At",
            render: (row) => formatDate(row.endAt),
        },
        {
            key: "logs",
            title: "Log",
            disableSort: true,
            render: (row) => (
                <Typography variant="body2" noWrap sx={{maxWidth: 400}}>
                    {firstLine(row.logs)}
                </Typography>
            ),
        },
        {
            key: "controlPanel",
            title: "",
            disableSort: true,
            render: (row) => (
                <Stack direction="row" justifyContent="center">
                    <IconButton onClick={() => setViewLog(row)} title="View full log">
                        <VisibilityOutlinedIcon/>
                    </IconButton>
                </Stack>
            ),
        },
    ], []);

    return (
        <Box>
            <Typography variant="h4" sx={{mb: 2}}>Scheduler Logs</Typography>
            <FiltersCluster>
                <DateTime
                    label="Start At - from"
                    value={filter.createdAtFrom ? dayjs(filter.createdAtFrom) : null}
                    onChange={(v: Dayjs | null) => setFilter({
                        ...filter,
                        page: 0,
                        createdAtFrom: v ? v.valueOf() : undefined,
                    })}
                />
                <DateTime
                    label="Start At - before"
                    value={filter.createdAtTo ? dayjs(filter.createdAtTo) : null}
                    onChange={(v: Dayjs | null) => setFilter({
                        ...filter,
                        page: 0,
                        createdAtTo: v ? v.valueOf() : undefined,
                    })}
                />
            </FiltersCluster>

            <Table
                data={data?.content || []}
                sort={{
                    key: filter.sortKey,
                    order: filter.sortOrder,
                    onChange: (key, order) => setFilter({...filter, page: 0, sortKey: key, sortOrder: order}),
                }}
                isLoading={isFetching}
                total={data?.totalElements || 0}
                page={filter.page}
                size={filter.size}
                headers={headers}
                pageChange={(page, size) => setFilter({...filter, page, size})}
            />

            {viewLog && (
                <Dialog open onClose={() => setViewLog(null)} maxWidth="md" fullWidth>
                    <DialogTitle>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <span>Log Details — {formatDate(viewLog.createdAt)}</span>
                            <IconButton onClick={() => setViewLog(null)}>
                                <CloseOutlinedIcon/>
                            </IconButton>
                        </Stack>
                    </DialogTitle>
                    <DialogContent dividers>
                        <Box
                            component="pre"
                            sx={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                fontFamily: "monospace",
                                fontSize: "0.85rem",
                                margin: 0,
                            }}
                        >
                            {viewLog.logs || "(no logs)"}
                        </Box>
                    </DialogContent>
                </Dialog>
            )}
        </Box>
    );
};
